import axios from "axios";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { NotificationManager } from "react-notifications";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate } from "react-router-dom";

import DownArrow from "../../assets/svgs/down-arrow.svg";
import Plus from "../../assets/svgs/Plus.svg";
import sortAsce from "../../assets/svgs/sort-ascending.svg";
import sortDesc from "../../assets/svgs/sort-descending.svg";
import swap from "../../assets/svgs/swap-vertical.svg";
import UpArrow from "../../assets/svgs/up-arrow.svg";

import ActionsMenu from "../../components/ActionsMenu";
import BarData from "../../components/Charts/BarData";
import DeleteConfirmationModal from "../../components/ConfirmationModals/DeleteConfirmationModal";
import Loader from "../../components/Loader";
import ContactPagination from "../../components/Pagination/ContactPagination";
import useAuth from "../../hooks/useAuth";
import BaseLayout from "../../layouts/BaseLayout";
import { BASE_URL } from "../../utils/Element";
import { initialUnderContractFilterData } from "../../utils/initialData";
import ExecutedFilter from "../Executed LOI/component/ExecutedFilter";
import LeadDataContainer from "../Leads/components/LeadDataContainer";
import AddUnderContractModal from "./AddUnderContractModal";
import ConvertClosedModal from "./components/ConvertClosedModal";
import FellOutContract from "./components/FellOutContract";
import EditUnderContractData from "./EditUnderContractModal";

// Custom debounce hook
const useDebounce = (value, delay = 600) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const UnderContract = () => {
  const [config] = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isSidebarCollapsed = useSelector((state) => state.isSidebarCollapsed);

  // ==================== States ====================
  const [loading, setLoading] = useState(true);
  const [listData, setListData] = useState([]);
  const [undercontractDashboard, setUnderContractDashboard] = useState(null);

  const [selectedId, setSelectedId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemPerPage, setItemPerPage] = useState(50);
  const [sortBy, setSortBy] = useState("id");
  const [sortDirection, setSortDirection] = useState("asc");
  const [expandedRows, setExpandedRows] = useState({});

  // Pagination
  const [paginationData, setPaginationData] = useState({
    totalItems: 0,
    from: 0,
    to: 0,
    totalPages: 0,
  });

  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [disableDelete, setDisableDelete] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showfelloutcontract, setShowFellOutContract] = useState(false);

  // Filters
  const [filterData, setFilterData] = useState(() => {
    const localFilterData = localStorage.getItem("filterData");
    return localFilterData ? JSON.parse(localFilterData) : initialUnderContractFilterData;
  });

  const [filterStatus, setFilterStatus] = useState({
    new_under_contract_quarter: false,
    total_under_contract_transaction: false,
    under_contract_buyer_transaction: false,
    under_contract_seller_transaction: false,
    under_contrac_gcl_to_company_ytd: false,
    avg_commission_per_transaction_ytd: false,
    buyer_initial_offer_to_under_contract_conversion_rate: false,
    exclusive_under_contract_conversion: false,
    jan: false, feb: false, mar: false, apr: false, may: false,
    jun: false, jul: false, aug: false, sep: false, oct: false,
    nov: false, dec: false,
    gci_closing_quarter1: false,
    gci_closing_quarter2: false,
    gci_closing_quarter3: false,
    gci_closing_quarter4: false,
  });

  // Search
  const queryParams = new URLSearchParams(location.search);
  const [searchInput, setSearchInput] = useState(queryParams.get('search') ?? '');
  const debouncedSearch = useDebounce(searchInput, 600);
  const [search, setSearch] = useState(queryParams.get('search') ?? '');

  // ==================== Dropdown View (Default: Team Under Contract) ====================
  const viewOptions = [
    { value: 0, label: "My Dashboard" },
    { value: 1, label: "My Under Contract" },
    { value: 2, label: "Team Dashboard" },
    { value: 3, label: "Team Under Contract" },
  ];

  const [activeView, setActiveView] = useState(3); // Default: Team Under Contract

  // ==================== Effects ====================
  useEffect(() => {
    if (debouncedSearch.length >= 3 || debouncedSearch === '') {
      setSearch(debouncedSearch);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (search.trim()) {
      params.set('search', search);
    } else {
      params.delete('search');
    }
    navigate({ search: params.toString() }, { replace: true });
  }, [search, navigate, location.search]);

  // Fetch data when view or dependencies change
  useEffect(() => {
    if (activeView === 1 || activeView === 3) {
      fetchData(filterData);
    } else {
      fetchDashboardData();
    }
  }, [activeView, currentPage, itemPerPage, search, sortBy, sortDirection]);

  useEffect(() => {
    if (activeView === 0 || activeView === 2) {
      fetchDashboardData();
    }
  }, [activeView]);

  // ==================== API Functions ====================
  const buildQueryParams = (filters) => {
    let params = `&page=${currentPage}&per_page=${itemPerPage}`;

    if (search) {
      params += `&search=${encodeURIComponent(search)}`;
    }

    const {
      created_at, property_name, noi, created_end_date, created_start_date,
      min_contract_price, max_contract_price, min_contract_cap_rate, max_contract_cap_rate,
      min_asking_price, max_asking_price, broker
    } = filters;

    if (created_at) params += `&created_at=${encodeURIComponent(created_at)}`;
    if (created_start_date) params += `&created_start_date=${encodeURIComponent(created_start_date)}`;
    if (created_end_date) params += `&created_end_date=${encodeURIComponent(created_end_date)}`;
    if (property_name) params += `&property_name=${encodeURIComponent(property_name)}`;
    if (noi) params += `&noi=${encodeURIComponent(noi)}`;
    if (broker) params += `&broker=${encodeURIComponent(broker)}`;

    if (min_contract_price) params += `&min_contract_price=${encodeURIComponent(min_contract_price)}`;
    if (max_contract_price) params += `&max_contract_price=${encodeURIComponent(max_contract_price)}`;
    if (min_contract_cap_rate) params += `&min_contract_cap_rate=${encodeURIComponent(min_contract_cap_rate)}`;
    if (max_contract_cap_rate) params += `&max_contract_cap_rate=${encodeURIComponent(max_contract_cap_rate)}`;
    if (min_asking_price) params += `&min_asking_price=${encodeURIComponent(min_asking_price)}`;
    if (max_asking_price) params += `&max_asking_price=${encodeURIComponent(max_asking_price)}`;

    // All filterStatus preserved
    Object.entries(filterStatus).forEach(([key, value]) => {
      if (value) params += `&${key}=true`;
    });

    if (sortBy !== "id") {
      params += `&sort_by=${sortBy}&sort_direction=${sortDirection}`;
    }

    return params;
  };

  const fetchData = (filters = filterData) => {
    setLoading(true);
    const queryParams = buildQueryParams(filters);

    axios
      .get(`${BASE_URL}/contract-listing?active_tab=${activeView}${queryParams}`, config)
      .then((res) => {
        const value = res?.data?.contract || {};
        setListData(value?.data || []);
        setPaginationData({
          totalItems: value?.total ?? 0,
          from: value?.from ?? 0,
          to: value?.to ?? 0,
          totalPages: value?.last_page ?? 0,
        });
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const fetchDashboardData = () => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/dashboard-under-contract?active_tab=${activeView}`, config)
      .then((res) => {
        setUnderContractDashboard(res?.data?.under_contract_dashboard);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setLoading(false));
  };

  const onSuccess = (filters) => {
    if (filters) setFilterData(filters);
    if (activeView === 1 || activeView === 3) {
      fetchData(filters || filterData);
    } else {
      fetchDashboardData();
    }
  };

  const BarGraphData = [
    { name: "Q1", count: undercontractDashboard?.gci_closing_quarter1, key: "gci_closing_quarter1" },
    { name: "Q2", count: undercontractDashboard?.gci_closing_quarter2, key: "gci_closing_quarter2" },
    { name: "Q3", count: undercontractDashboard?.gci_closing_quarter3, key: "gci_closing_quarter3" },
    { name: "Q4", count: undercontractDashboard?.gci_closing_quarter4, key: "gci_closing_quarter4" },
  ];

  const BarGraphData2 = [
    { name: "Jan", count: undercontractDashboard?.Jan, key: "jan" },
    { name: "Feb", count: undercontractDashboard?.Feb, key: "feb" },
    { name: "Mar", count: undercontractDashboard?.Mar, key: "mar" },
    { name: "Apr", count: undercontractDashboard?.Apr, key: "apr" },
    { name: "May", count: undercontractDashboard?.May, key: "may" },
    { name: "Jun", count: undercontractDashboard?.Jun, key: "jun" },
    { name: "Jul", count: undercontractDashboard?.Jul, key: "jul" },
    { name: "Aug", count: undercontractDashboard?.Aug, key: "aug" },
    { name: "Sep", count: undercontractDashboard?.Sept, key: "sep" },
    { name: "Oct", count: undercontractDashboard?.Oct, key: "oct" },
    { name: "Nov", count: undercontractDashboard?.Nov, key: "nov" },
    { name: "Dec", count: undercontractDashboard?.Dec, key: "dec" },
  ];

  // ==================== Handlers ====================
  const handleViewChange = (e) => {
    const newView = parseInt(e.target.value);
    setActiveView(newView);
    setCurrentPage(1);
    setExpandedRows({});

    // Reset filterStatus when switching between Dashboard and List views
    const wasDashboard = activeView === 0 || activeView === 2;
    const isNowDashboard = newView === 0 || newView === 2;

    if (wasDashboard !== isNowDashboard) {
      setFilterStatus({
        new_under_contract_quarter: false,
        total_under_contract_transaction: false,
        under_contract_buyer_transaction: false,
        under_contract_seller_transaction: false,
        under_contrac_gcl_to_company_ytd: false,
        avg_commission_per_transaction_ytd: false,
        buyer_initial_offer_to_under_contract_conversion_rate: false,
        exclusive_under_contract_conversion: false,
        jan: false, feb: false, mar: false, apr: false, may: false,
        jun: false, jul: false, aug: false, sep: false, oct: false,
        nov: false, dec: false,
        gci_closing_quarter1: false,
        gci_closing_quarter2: false,
        gci_closing_quarter3: false,
        gci_closing_quarter4: false,
      });
    }
  };

  const handleBoxClick = (key) => {
    const targetView = activeView === 0 ? 1 : 3;
    setActiveView(targetView);
    setCurrentPage(1);

    const newFilterStatus = {
      new_under_contract_quarter: false,
      total_under_contract_transaction: false,
      under_contract_buyer_transaction: false,
      under_contract_seller_transaction: false,
      under_contrac_gcl_to_company_ytd: false,
      avg_commission_per_transaction_ytd: false,
      buyer_initial_offer_to_under_contract_conversion_rate: false,
      exclusive_under_contract_conversion: false,
      jan: false, feb: false, mar: false, apr: false, may: false,
      jun: false, jul: false, aug: false, sep: false, oct: false,
      nov: false, dec: false,
      gci_closing_quarter1: false,
      gci_closing_quarter2: false,
      gci_closing_quarter3: false,
      gci_closing_quarter4: false,
    };

    if (typeof key === "string") {
      newFilterStatus[key] = true;
    }
    setFilterStatus(newFilterStatus);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    const trimmed = newValue.startsWith(" ") ? newValue.trimStart() : newValue;
    setSearchInput(trimmed);
  };
  const handleDelete = () => {
    setDisableDelete(true);
    axios
      .delete(`${BASE_URL}/delete-contract/${selectedId}`, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowDeleteModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      })
      .finally(() => setDisableDelete(false));
  };

  const toggleRowDetails = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleNext = () => {
    if (currentPage !== paginationData.totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleConfirm = () => {
    axios
      .post(`${BASE_URL}/mark-contract/${selectedId}`, { mark_contract: true }, config)
      .then((res) => {
        setSelectedId("");
        onSuccess();
        setShowConfirmModal(false);
        if (res?.data?.message) {
          NotificationManager.success(res?.data?.message);
        }
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };


  // ==================== JSX ====================
  return (
    <BaseLayout>
      <div className="contact-header mb-3">
        <p className="head-1 dark-H">Under Contract</p>

        <div className="md:flex block gap-6 items-center">
          <div className="flex gap-6 justify-between items-center w-full md:w-auto flex-wrap">
            {/* Dropdown - Team Under Contract is Default */}
            <div className="relative min-w-[240px]">
              <select
                value={activeView}
                onChange={handleViewChange}
                className="w-full bg-white rounded-lg px-4 py-2.5 body-S dark-M cursor-pointer appearance-none"
              >
                {viewOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                <img src={DownArrow} alt="down" className="w-4 h-4" />
              </div>
            </div>

            <ExecutedFilter
              filterData={filterData}
              onSetFilterData={setFilterData}
              from="Under Contract"
              onCallApiAgain={onSuccess}
            />

            <div className="search-box contacts relative flex-1 min-w-[250px]">
              <input
                type="text"
                className="body-S w-full"
                placeholder="Search name, address, city, state"
                value={searchInput}
                onChange={handleInputChange}
                style={{ border: "1px solid #D8D8D8" }}
              />
            </div>

            <button
              className="add-contact-button green-bg-H light-L body-S whitespace-nowrap"
              onClick={() => setShowAddModal(true)}
            >
              <img className="mr-2 sidebar-icons" src={Plus} alt="plus" /> Add
            </button>
          </div>
        </div>
      </div>

      {/* ==================== LIST VIEWS ==================== */}
      {(activeView === 1 || activeView === 3) && (
        <div className="light-bg-L pb-3 table-container">
          <div className={`${isSidebarCollapsed ? "collapsed-width" : "width"} table-info light-bg-L`}>
            {loading ? (
              <Loader />
            ) : listData?.length === 0 ? (
              <p className="body-N text-center mt-6 p-10">No Data Available</p>
            ) : (
              <table className="contact-table light-bg-L">
                <thead>
                  <tr className="uppercase body-N dark-M">
                    <th><p>Details</p></th>
                    <th className="green-H">
                      <div className="table-header">
                        Contract Date
                        <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        PROPERTY NAME
                        <img role="button" src={sortBy !== "property_name" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("property_name"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        NOI
                        <img role="button" src={sortBy !== "created_at" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("created_at"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        CONTRACT PRICE
                        <img role="button" src={sortBy !== "contract_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        CONTRACT CAP RATE
                        <img role="button" src={sortBy !== "contract_cap_rate" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("contract_cap_rate"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        % ASKING PRICE
                        <img role="button" src={sortBy !== "asking_price" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("asking_price"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th>
                      <div className="table-header">
                        GCI TO COMPANY
                        <img role="button" src={sortBy !== "company_gcl" ? swap : sortDirection === "desc" ? sortDesc : sortAsce} alt="sort" onClick={() => { setSortBy("company_gcl"); setSortDirection(sortDirection === "desc" ? "asc" : "desc"); }} />
                      </div>
                    </th>
                    <th><div className="table-header">Actions</div></th>
                  </tr>
                </thead>
                <tbody>
                  {listData?.map((el, idx) => (
                    <React.Fragment key={idx}>
                      <tr className="dark-H body-N">
                        <td>
                          <img role="button" src={expandedRows[el.id] ? UpArrow : DownArrow} alt="toggle" className="sidebar-icons" onClick={() => toggleRowDetails(el.id)} />
                        </td>
                        <td>{moment(el?.created_at).tz('America/New_York').format("MM/DD/YY")}</td>
                        <td className="head-5 capitalize">
                          <Link
                            to="#"
                            onClick={(e) => {
                              e.preventDefault();
                              if (el?.portfolio === 0) navigate(`/property/${el?.executed?.property_id}`);
                              else if (el?.executed?.link?.owner_info_type === "contact") navigate(`/contact/${el?.executed?.link?.owner?.id}`);
                              else if (el?.executed?.link?.owner_info_type === "company") navigate(`/company/${el?.executed?.link?.owner?.id}`);
                            }}
                          >
                            {el?.executed?.link?.property_name}<br />
                            {el?.executed?.link?.street_address}<br />
                            {el?.executed?.link?.city && el?.executed?.link?.state ? `${el.executed.link.city}, ${el.executed.link.state}` : ""}
                          </Link>
                        </td>
                        <td className="text-center">${el?.executed?.link?.noi_format}</td>
                        <td className="text-center">${el?.contract_price_format}</td>
                        <td className="text-center">{el?.contract_cap_rate_format}%</td>
                        <td className="text-center">{el?.asking_price_format_percentage}%</td>
                        <td className="text-center">${el?.executed?.gross_commission?.gci_to_company}</td>
                        <td className="flex gap-3 items-center justify-center">
                          <ActionsMenu
                            from="under-contract"
                            handleEdit={() => {
                              setShowEditModal(true)
                              setSelectedId(el?.id)
                            }}
                            handleDelete={() => {
                              setSelectedId(el?.id)
                              setShowDeleteModal(true)
                            }}
                            showOtherOption={true}
                            otherOptionTitle={"Convert to Closed"}
                            handleOtherOption={() => {
                              setShowConfirmModal(true)
                              setSelectedId(el?.id)
                            }}
                            felloutcontractoption={"Fell Out Of Contract"}
                            handlefelloutcontractOption={() => {
                              setShowFellOutContract(true)
                              setSelectedId(el?.id)
                            }}
                          />
                        </td>
                      </tr>

                      {expandedRows[el.id] && (
                        <tr>
                          <td colSpan="11">
                            <div className="expanded-row-content p-4 bg-[rgb(244,244,244)] rounded-md">
                              <div className="">
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-6 items-start">
                                  {/* Acquisition */}
                                  <div>
                                    <p className="dark-H head-4">Acquisition</p>
                                    <Link
                                      to={`/contact/${el?.executed?.offer?.buyer_contact_id}`}
                                      className="body-N dark-H"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        navigate(`/contact/${el?.executed?.offer?.buyer_contact_id}`)
                                      }}
                                    >
                                      {el?.executed?.offer?.acquistion_name || ""}
                                    </Link>
                                  </div>

                                  {/* Disposition */}
                                  <div>
                                    <p className="dark-H head-4">Disposition</p>
                                    <Link
                                      to={`/contact/${el?.executed?.offer?.seller_contact_id}`}
                                      className="body-N dark-H"
                                      onClick={(e) => {
                                        e.preventDefault()
                                        navigate(`/contact/${el?.executed?.offer?.seller_contact_id}`)
                                      }}
                                    >
                                      {el?.executed?.offer?.disposition_name || ""}
                                    </Link>
                                  </div>

                                  {/* Asking Cap Rate */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Asking Cap Rate</p>
                                    <p className="body-N dark-H">
                                      {el?.link?.asking_cap_rate_format ? `${el?.link?.asking_cap_rate_format}%` : "0"}
                                    </p>
                                  </div>

                                  {/* Asking Price */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Asking Price</p>
                                    <p className="body-N dark-H">
                                      {el?.link?.asking_price_format ? `$${el?.link?.asking_price_format}` : "0"}
                                    </p>
                                  </div>

                                  {/* Seller Broker */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.executed?.offer?.seller_broker?.length > 0 ? (
                                        el.executed.offer.seller_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.executed.offer.seller_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Seller BDS */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.executed?.offer?.seller_bds?.first_name} {el?.executed?.offer?.seller_bds?.last_name || ""}
                                    </p>
                                  </div>

                                  {/* Buyer Broker */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div className="flex flex-wrap gap-x-2 gap-y-1 body-N dark-H">
                                      {el?.executed?.offer?.buyer_broker?.length > 0 ? (
                                        el.executed.offer.buyer_broker.map((broker, index) => (
                                          <span key={broker.id || index} className="whitespace-nowrap">
                                            {broker?.first_name || ""} {broker?.last_name || ""}
                                            {index < el.executed.offer.buyer_broker.length - 1 ? "," : ""}
                                          </span>
                                        ))
                                      ) : (
                                        <span>—</span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Buyer BDS */}
                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N dark-H">
                                      {el?.executed?.offer?.buyer_bds?.first_name} {el?.executed?.offer?.buyer_bds?.last_name || ""}
                                    </p>
                                  </div>
                                </div>


                                <p className="head-3 dark-H my-6 text-center">GROSS COMMISSION INCOME BREAKDOWN</p>

                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_seller?.toLocaleString() || 0}
                                      </p>
                                      {el?.executed?.gcl_broker_seller_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.executed?.gcl_broker_seller_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer Broker</p>
                                    <div>
                                      <p className="body-N dark-H">
                                        ${el?.gross_commission?.gci_to_broker_buyer?.toLocaleString() || 0}
                                      </p>
                                      {el?.executed?.gcl_broker_buyer_share?.length > 0 && (
                                        <div className="mt-1">
                                          {el.executed?.gcl_broker_buyer_share.map((item, index) => (
                                            <p key={index} className="body-N dark-H">
                                              {item.name} ({item.share})
                                            </p>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>


                                  <div>
                                    <p className="dark-H head-4 mb-2">Seller BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_seller
                                        ? `$${el.gross_commission?.gci_to_broker_representing_seller.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>

                                  <div>
                                    <p className="dark-H head-4 mb-2">Buyer BDS</p>
                                    <p className="body-N  dark-H ">
                                      {el?.gross_commission?.gci_to_broker_representing_buyer
                                        ? `$${el.gross_commission?.gci_to_broker_representing_buyer.toLocaleString()}`
                                        : "$0"}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {listData?.length > 0 && (
            <ContactPagination
              paginationData={paginationData}
              activeTab={activeView}
              handleNext={handleNext}
              handlePrev={handlePrev}
              onSuccess={onSuccess}
              itemPerPage={itemPerPage}
              handleItemPerPageSelect={(value) => {
                setItemPerPage(value);
                setCurrentPage(1);
              }}
            />
          )}
        </div>
      )}

      {/* ==================== DASHBOARD VIEWS ==================== */}
      {(activeView === 0 || activeView === 2) && (
        <>
          <div className="flex gap-3">
            <div className="w-[70%]">
              <div className="flex items-center gap-3 flex-wrap">
                <LeadDataContainer text="NEW UNDER CONTRACT THIS QUARTER" number={undercontractDashboard?.new_under_contract_quarter} onClick={() => handleBoxClick("new_under_contract_quarter")} />
                <LeadDataContainer text="TOTAL TRANSACTIONS UNDER CONTRACT YTD" number={undercontractDashboard?.total_under_contract_transaction} onClick={() => handleBoxClick("total_under_contract_transaction")} />
                <LeadDataContainer text="BUYER TRANSACTIONS UNDER CONTRACT YTD" number={undercontractDashboard?.under_contract_buyer_transaction} onClick={() => handleBoxClick("under_contract_buyer_transaction")} />
                <LeadDataContainer text="SELLER TRANSACTIONS UNDER CONTRACT YTD" number={undercontractDashboard?.under_contract_seller_transaction} onClick={() => handleBoxClick("under_contract_seller_transaction")} />
              </div>

              <div className="flex gap-4 items-center mt-4">
                <BarData Title="GROSS HOPPER GAIN BY QUARTER" BarGraphData={BarGraphData} className="w-[50%]" onBarClick={handleBoxClick} />
                <BarData Title="GCI CLOSING PROJECTIONS" BarGraphData={BarGraphData2} className="w-[50%]" onBarClick={handleBoxClick} />
              </div>
            </div>

            <div className="w-[30%]">
              <LeadDataContainer text="UNDER CONTRACT GCI TO COMPANY" number={`$${undercontractDashboard?.under_contrac_gcl_to_company_ytd ?? 0}`} onClick={() => handleBoxClick("under_contrac_gcl_to_company_ytd")} />
              <LeadDataContainer text="AVG. COMMISSION PER TRANSACTION" number={`$${undercontractDashboard?.avg_commission_per_transaction_ytd ?? 0}`} onClick={() => handleBoxClick("avg_commission_per_transaction_ytd")} />
              <LeadDataContainer text="BUYER INTIAL OFFER TO UC CONVERSION RATE" number={undercontractDashboard?.buyer_initial_offer_to_under_contract_conversion_rate} onClick={() => handleBoxClick("buyer_initial_offer_to_under_contract_conversion_rate")} />
              <LeadDataContainer text="EXCLUSIVE TO UC CONVERSION RATE" number={undercontractDashboard?.exclusive_under_contract_conversion} onClick={() => handleBoxClick("exclusive_under_contract_conversion")} />
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      <AddUnderContractModal showModal={showAddModal} onClose={() => setShowAddModal(false)} onCallApi={onSuccess} />
      <EditUnderContractData showModal={showEditModal} onClose={() => setShowEditModal(false)} id={selectedId} onCallApi={() => { setSelectedId(""); onSuccess(); }} />
      <ConvertClosedModal showModal={showConfirmModal} id={selectedId} onClose={() => setShowConfirmModal(false)} handleConfirm={handleConfirm} />
      <FellOutContract from="undercontact" showModal={showfelloutcontract} id={selectedId} onClose={() => setShowFellOutContract(false)} onCallApi={() => { setSelectedId(""); onSuccess(); }} />
      <DeleteConfirmationModal showModal={showDeleteModal} disable={disableDelete} onClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />
    </BaseLayout>
  );
};

export default UnderContract;